import './orders.scss';

import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ReplayIcon from '@mui/icons-material/Replay';
import { Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { WHandDeliveryIcon } from '@wareclouds/wareclouds-ui';

import { NOTIFICATION_TYPES } from '../../config/consts/commons';
import { handleAxiosErrorMessage } from '../../utils';

import { getRouteWareclouds, getCities } from './services';

import { setMessage } from '../toasts/toastSlice';
import {
  setCities,
  setWareclouds,
  closeToDeliverToClientModals,
} from './orderSlice';

import OrdersToDeliver from './OrdersToDeliver';
import OrdersToDeliverModal from './OrdersToDeliverModal';
import OrdersToDeliverPickupType11Modal from './OrdersToDeliverPickupType11Modal';
import OrdersToReportModal from './OrdersToReportModal';
import OrdersToRetrieveModal from './OrdersToRetrieveModal';
import OrdersToReturn from './OrdersToReturn';
import OrdersToReturnModal from './OrdersToReturnModal';
import SearchBar from './OrderSearchBar';
import OpenMapRouteDialog from './openMapRouterDialog';
import { ToReceive } from './toReceive/ui';
import { useGetClouderServices } from '../common/services';

const RETURN_SERVICE_ID = 13;
const RETURN_SERVICE_KEY = 'RETURN_SERVICE';

const OrdersPage = () => {
  const {
    ordersModalConfig,
    toDeliverCounter,
    toReturnCounter,
    toRetrieveCounter,
    dialogs,
  } = useSelector((state) => state.orders);
  const { show: showModal, modalType } = ordersModalConfig;
  const { t } = useTranslation(['system', 'button', 'description']);

  const [activeTab, setActiveTab] = useState('toRetrieve');

  const dispatch = useDispatch();
  const { data: clouderServices } = useGetClouderServices();

  const hasReturnService = clouderServices?.data?.some(
    ({ id_service, service_key_name }) =>
      id_service === RETURN_SERVICE_ID ||
      service_key_name === RETURN_SERVICE_KEY
  );

  const orderTypeTabs = {
    toRetrieve: <ToReceive />,
    toDeliver: <OrdersToDeliver />,
    toReturn: <OrdersToReturn />,
  };

  const orderTypeModals = {
    toRetrieve: <OrdersToRetrieveModal />,
    toDeliver: <OrdersToDeliverModal />,
    toDeliverType11: <OrdersToDeliverPickupType11Modal />,
    toReturn: <OrdersToReturnModal />,
    toReport: <OrdersToReportModal />,
    toReportPickupType11: <OrdersToReportModal />,
  };

  useEffect(() => {
    const clouderApi = async () => {
      try {
        const citiesResponse = await getCities();

        const warecloudsResponse = await getRouteWareclouds();

        const cleanCities = citiesResponse.data.map(
          ({ id_city, city_name }) => ({ id: id_city, value: city_name })
        );

        const cleanWareclouds = warecloudsResponse.data.map(
          ({ id_warecloud, first_name, last_name }) => ({
            id: id_warecloud,
            value: `${first_name} ${last_name}`,
          })
        );

        dispatch(setWareclouds(cleanWareclouds));
        dispatch(setCities(cleanCities));
      } catch (err) {
        const errorMessage = handleAxiosErrorMessage(err);
        dispatch(
          setMessage({
            body: t(errorMessage, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
    };

    clouderApi();
  }, [dispatch, t]);

  const renderTab = () => {
    return orderTypeTabs[activeTab];
  };

  const renderModal = () => {
    return orderTypeModals[modalType];
  };

  const renderCounter = (payload) => {
    if (payload === null) {
      return <Spinner animation="grow" size="sm" />;
    }
    return payload;
  };

  return (
    <Grid container justifyContent="center" mb={3}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: 2,
        }}
      >
        {showModal && renderModal()}

        <SearchBar
          placeholder={t('order_search_bar_placeholder', {
            ns: 'description',
          })}
        />

        {dialogs.mapRoute && (
          <OpenMapRouteDialog
            open={dialogs.mapRoute}
            onClose={() => dispatch(closeToDeliverToClientModals('mapRoute'))}
          />
        )}

        <Tab.Container id="tabs" defaultActiveKey={activeTab}>
          <Row className="switch mt-4 mb-3">
            <Nav variant="pills" className="w-100 h-100 p-0">
              <Nav.Item className="switch__item">
                <Nav.Link
                  className="switch__link switch__link--rounded-left"
                  eventKey="toRetrieve"
                  onClick={() => setActiveTab('toRetrieve')}
                >
                  <HomeIcon className="switch__icon" />
                  {t('tabs.receive', { ns: 'button' })} (
                  {renderCounter(toRetrieveCounter)})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item className="switch__item">
                <Nav.Link
                  className="switch__link"
                  eventKey="toDeliver"
                  onClick={() => setActiveTab('toDeliver')}
                >
                  <WHandDeliveryIcon className="switch__icon" />
                  {t('tabs.deliver', { ns: 'button' })} (
                  {renderCounter(toDeliverCounter)})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item className="switch__item">
                <Nav.Link
                  className="switch__link switch__link--rounded-right"
                  eventKey="toReturn"
                  onClick={() => setActiveTab('toReturn')}
                >
                  <ReplayIcon className="switch__icon" />
                  {t('tabs.to_return', { ns: 'button' })} (
                  {renderCounter(toReturnCounter)})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Row>

          {renderTab()}
        </Tab.Container>
      </Grid>
    </Grid>
  );
};

export default OrdersPage;
